import { StatefulService } from '@app/service/stateful.service';
import { FlowInstance } from './flow.service';
import { PageInstance } from './page.service';
import { dependencies } from './workflow.dependency';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { AllowedStreamlinePrefillTypes } from '@workflow/ecp-shared/enum/enum';

/**
 * A class to execute logic of a page action
 */
export class ActionPage {
  constructor(
    private readonly page: PageInstance,
    private readonly flow: FlowInstance,
    private readonly statefulService: StatefulService,
    private readonly workflowLoader: WorkflowLoaderService
  ) {
    if (!page.isActionPage) {
      throw new Error(`Page ${page.id} is not an action page`);
    }
  }

  /**
   * Because action pages are async we need to show an indicator, currently we show button indicator of the previous page
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public run(): Promise<any> {
    // Because action pages won't be rendered we can show loading indicator on the previous page
    // ToDo: Implement find the last actual page in case previous page is an action page
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const previousPage = this.page.previousPage || ({} as any);

    previousPage.actionLogicIsFailed = false;
    // Show streamline loader if streamlined
    if (this.flow.config.streamlined) {
      const supportedAutoFillValue: string[] = AllowedStreamlinePrefillTypes;
      const streamlineSkip = this.statefulService.wrap(this.flow.config.streamlinedPrefillPath, null, this.page.pageIterationId);
      if (this.flow.config.streamlinedSkipPages.includes(this.page.id)) {
        this.workflowLoader.start('Retrieving your information...', {
          animationId: supportedAutoFillValue.includes(streamlineSkip.value?.toString()) ? 'documentscan' : 'default',
          overlayMode: 'card',
          segments: this.flow.config.streamlinedSkipPages.length - 1
        });
      }
    }
    previousPage.actionButtonIsLoading = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = dependencies.logicService.evaluate<Promise<boolean>>(
      this.page.config.actionLogic,
      {
        page: this.page
      },
      'generic'
    )[0];

    return result
      .then((res) => {
        previousPage.actionButtonIsLoading = false;
        return res;
      })
      .catch(() => {
        previousPage.actionButtonIsLoading = false;
        previousPage.actionLogicIsFailed = true;
        this.onError(previousPage);
        this.workflowLoader.hide();
      });
  }

  private onError(previousPage: PageInstance): void {
    if (typeof this.page.config.errorOnActionLogic === 'string') {
      if (previousPage?.config) previousPage.config.errorOnActionLogic = this.page.config.errorOnActionLogic;
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    const result = dependencies.logicService.evaluate<Promise<any>>(
      this.page.config.errorOnActionLogic,
      {
        page: this.page
      },
      'generic'
    )[0];
  }
}
